import React, { useEffect, useState } from "react"
import { FaPencilAlt } from "react-icons/fa"
import { MdBlock, MdCheck, MdCode, MdContentCopy, MdInfoOutline, MdLanguage } from "react-icons/md"
import { toast } from "react-toastify"
import Button from "../Form/Button"
import FormGroup from "../Form/FormGroup"
import FormGroupInline from "../Form/FormGroupInline"
import Input from "../Form/Input"
import Modal from "./index"
import styles from "./modalApiDomain.module.css"

const copyToClipboard = async value => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
        return
    }

    const textarea = document.createElement("textarea")
    textarea.value = value
    textarea.setAttribute("readonly", "")
    textarea.style.position = "absolute"
    textarea.style.left = "-9999px"
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand("copy")
    document.body.removeChild(textarea)
}

const ModalApiDomain = ({ isNew = false, value = {}, editable = false, setEdit, onSubmit, closeModal }) => {
    const [domain, setDomain] = useState(value)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        setDomain(value)
    }, [value])

    const handleChangeValue = (name, value) => {
        setDomain(previousValue => ({ ...previousValue, [name]: value }))
    }

    const handleCancel = event => {
        event.preventDefault()
        if (isNew) {
            closeModal()
        } else {
            setEdit(false)
            setDomain(value)
        }
    }

    const handleCopyScript = async () => {
        if (!domain?.script) return
        try {
            await copyToClipboard(domain.script)
            setCopied(true)
            toast.success("Script copiado.")
        } catch (_) {
            toast.error("Não foi possível copiar o script. Selecione o código e copie manualmente.")
        }
    }

    const title = isNew
        ? "Conectar um domínio"
        : editable
            ? `Editar domínio: ${domain.name || "-"}`
            : "Instalar captura de formulários"
    const description = isNew
        ? "Autorize o site que enviará cadastros para a Oktto."
        : editable
            ? "Altere o nome usado para identificar este domínio."
            : "Copie o script e siga as etapas para começar a receber cadastros."

    return (
        <Modal icon={isNew || editable ? <MdLanguage /> : <MdCode />} title={title} description={description} closeModal={closeModal}>
            <form onSubmit={event => onSubmit(event, isNew, domain)}>
                {editable ?
                    <>
                        <FormGroup labelText="Nome para identificação">
                            <Input type="text" placeholder="Ex.: Site institucional" value={domain.name ?? ""} required={true} onChange={event => handleChangeValue("name", event.target.value)} />
                            <small className={styles.fieldHelp}>Esse nome aparece apenas na Oktto e ajuda sua equipe a reconhecer o site.</small>
                        </FormGroup>
                        <FormGroup labelText="Domínio autorizado">
                            {isNew ?
                                <>
                                    <Input type="text" placeholder="exemplo.com.br" value={domain.domain ?? ""} required={true} onChange={event => handleChangeValue("domain", event.target.value.toLowerCase().trimStart())} />
                                    <small className={styles.fieldHelp}>Informe sem <strong>https://</strong> e sem caminho de página. Prefira o domínio principal, como <strong>exemplo.com.br</strong>, para também autorizar seus subdomínios.</small>
                                </>
                                : <span className={styles.readonlyDomain}>{domain.domain}</span>
                            }
                        </FormGroup>
                        {isNew &&
                            <div className={styles.authorizationNotice}>
                                <MdInfoOutline />
                                <div>
                                    <strong>O domínio e seus subdomínios serão autorizados</strong>
                                    <span>Por exemplo, ao conectar exemplo.com.br, páginas em www.exemplo.com.br também poderão enviar formulários.</span>
                                </div>
                            </div>
                        }
                        <FormGroupInline>
                            <Button variant="primary" type="submit">{isNew ? "Conectar domínio" : "Salvar alterações"}</Button>
                            <Button variant="secondary" type="button" onClick={handleCancel}>Cancelar</Button>
                        </FormGroupInline>
                    </>
                    :
                    <div className={styles.installation}>
                        <div className={styles.authorizedDomain}>
                            <div className={styles.authorizedIcon}>{domain.is_active === false ? <MdBlock /> : <MdCheck />}</div>
                            <div>
                                <strong>{domain.is_active === false ? "Captura bloqueada" : "Domínio autorizado"}</strong>
                                <span>{domain.domain}</span>
                            </div>
                            <Button variant="secondary" size="sm" fullWidth={false} type="button" onClick={() => setEdit(true)}><FaPencilAlt /> Editar nome</Button>
                        </div>

                        <ol className={styles.installationSteps}>
                            <li>
                                <span>1</span>
                                <div>
                                    <strong>Copie o script da Oktto</strong>
                                    <p>Use o botão para copiar o código completo, sem fazer alterações.</p>
                                </div>
                            </li>
                        </ol>

                        <div className={styles.codePanel}>
                            <div className={styles.codeHeader}>
                                <span>Script de captura</span>
                                <button type="button" onClick={handleCopyScript}>{copied ? <MdCheck /> : <MdContentCopy />}{copied ? "Copiado" : "Copiar script"}</button>
                            </div>
                            <pre><code>{domain.script || ""}</code></pre>
                        </div>

                        <ol className={styles.installationSteps} start="2">
                            <li>
                                <span>2</span>
                                <div>
                                    <strong>Instale antes do fechamento de &lt;/body&gt;</strong>
                                    <p>Adicione o script uma única vez em todas as páginas que possuem formulários. No Google Tag Manager, use uma tag HTML personalizada acionada em todas essas páginas.</p>
                                </div>
                            </li>
                            <li>
                                <span>3</span>
                                <div>
                                    <strong>Envie um cadastro de teste</strong>
                                    <p>Preencha e envie um formulário no site. A Oktto detectará automaticamente a página e os campos recebidos.</p>
                                </div>
                            </li>
                            <li>
                                <span>4</span>
                                <div>
                                    <strong>Configure o destino no Kanban</strong>
                                    <p>Volte à lista de formulários, abra o formulário detectado e combine os campos antes de importar os cadastros pendentes.</p>
                                </div>
                            </li>
                        </ol>

                        <div className={styles.installationNote}>
                            <MdInfoOutline />
                            <span>O script não altera o visual do site. Ele captura novos envios depois da instalação; cadastros anteriores não são recuperados.</span>
                        </div>

                        <details className={styles.advancedInstructions}>
                            <summary>Privacidade e casos especiais</summary>
                            <div>
                                <p>Campos de senha, arquivo e pagamento são ignorados automaticamente. Use <code>data-oktto-ignore</code> para ignorar um campo ou formulário completo.</p>
                                <p>Em páginas com formulários sem <code>id</code> ou <code>name</code>, você pode definir uma identidade estável, como <code>data-oktto-form="contato-principal"</code>.</p>
                                <p>Se o formulário possui um campo honeypot antispam, marque-o com <code>data-oktto-honeypot</code>; envios em que ele estiver preenchido serão descartados.</p>
                                <p>Sites com política CSP devem permitir os endereços da Oktto em <code>script-src</code> e <code>connect-src</code>. O script não deve ser instalado mais de uma vez.</p>
                                <p>Se o site usa consentimento, informe-o antes do envio com <code>{'window.okttoForms.setConsent({ ad_user_data: "GRANTED", ad_personalization: "GRANTED" })'}</code>. Cookies de anúncios só são coletados após consentimento concedido.</p>
                            </div>
                        </details>

                        <div className={styles.installationActions}>
                            <Button variant="primary" type="button" onClick={handleCopyScript}>{copied ? <MdCheck /> : <MdContentCopy />}{copied ? "Script copiado" : "Copiar script"}</Button>
                            <Button variant="secondary" type="button" onClick={closeModal}>Concluir</Button>
                        </div>
                    </div>
                }
            </form>
        </Modal>
    )
}

export default ModalApiDomain
